/**
 * Font Configuration
 * 
 * This configuration allows you to set two fonts across the site:
 * 1. headingFont - For headings, links, buttons, banners
 * 2. bodyFont - For body text, paragraphs, and general content
 * 
 * Both local and Google Fonts are supported.
 * 
 * Example configurations:
 * 
 * Using Google Fonts:
 * {
 *   headingFont: { type: 'google', family: 'Playfair Display', weights: [700, 900] },
 *   bodyFont: { type: 'google', family: 'Lato', weights: [400, 500, 700] }
 * }
 * 
 * Using Local Fonts:
 * {
 *   headingFont: { type: 'local', family: 'outfit-bold' },
 *   bodyFont: { type: 'local', family: 'outfit-regular' }
 * }
 * 
 * Mixed:
 * {
 *   headingFont: { type: 'google', family: 'Playfair Display', weights: [700, 900] },
 *   bodyFont: { type: 'local', family: 'outfit-regular' }
 * }
 */

// Default static configuration
const defaultFontConfig = {
  // Default configuration - using Lato for headings and Inter for body
  headingFont: {
    type: 'google', // 'google' or 'local'
    family: 'Lato', // For Google Fonts, use exact font name like 'Playfair Display'
    weights: [700, 900], // For Google Fonts, optionally specify weights
  },
  bodyFont: {
    type: 'google', // 'google' or 'local'
    family: 'Inter', // Changed to Inter
    weights: [300, 400, 500, 600, 700],
  },
};

// This will be set dynamically by the theme system
let fontConfig = { ...defaultFontConfig };

/**
 * Get the heading font family
 * @returns {string} Font family string for CSS
 */
export const getHeadingFont = () => {
  const font = fontConfig.headingFont;
  if (font.type === 'google') {
    return `"${font.family}", sans-serif`;
  }
  return `"${font.family}"`;
};

/**
 * Get the body font family
 * @returns {string} Font family string for CSS
 */
export const getBodyFont = () => {
  const font = fontConfig.bodyFont;
  if (font.type === 'google') {
    return `"${font.family}", sans-serif`;
  }
  return `"${font.family}"`;
};

/**
 * Get the font configuration
 * @returns {object} The entire font configuration
 */
export const getFontConfig = () => {
  return fontConfig;
};

/**
 * Get Google Fonts import URL
 * @returns {string|null} Google Fonts URL if using Google Fonts, null otherwise
 */
export const getGoogleFontsUrl = () => {
  const fonts = [];
  const fontWeights = {};

  if (fontConfig.headingFont.type === 'google') {
    const family = fontConfig.headingFont.family.replace(/\s+/g, '+');
    const weights = fontConfig.headingFont.weights || [400, 700];
    if (!fontWeights[family]) {
      fontWeights[family] = new Set();
    }
    weights.forEach(w => fontWeights[family].add(w));
  }

  if (fontConfig.bodyFont.type === 'google') {
    const family = fontConfig.bodyFont.family.replace(/\s+/g, '+');
    const weights = fontConfig.bodyFont.weights || [400];
    if (!fontWeights[family]) {
      fontWeights[family] = new Set();
    }
    weights.forEach(w => fontWeights[family].add(w));
  }

  Object.entries(fontWeights).forEach(([family, weights]) => {
    const weightStr = Array.from(weights).sort((a, b) => a - b).join(';');
    fonts.push(`family=${family}:wght@${weightStr}`);
  });

  if (fonts.length === 0) {
    return null;
  }

  return `https://fonts.googleapis.com/css2?${fonts.join('&')}&display=swap`;
};

/**
 * Get Tailwind font configuration
 * @returns {object} Object suitable for Tailwind fontFamily configuration
 */
export const getTailwindFontConfig = () => {
  return {
    heading: [getHeadingFont()],
    body: [getBodyFont()],
  };
};

/**
 * Update font configuration from theme data
 * @param {object} themeData - Theme data with fonts object
 */
export const updateFontConfigFromTheme = (themeData) => {
  if (themeData && themeData.fonts) {
    fontConfig = {
      headingFont: {
        type: 'google',
        family: themeData.fonts.heading || defaultFontConfig.headingFont.family,
        weights: [700, 900],
      },
      bodyFont: {
        type: 'google',
        family: themeData.fonts.body || defaultFontConfig.bodyFont.family,
        weights: [300, 400, 500, 600, 700],
      },
    };
  }
};

export { fontConfig as default };
